import React, { useContext, useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Link from "@material-ui/core/Link";
import CircularProgress from "@material-ui/core/CircularProgress";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { getTokens } from "../../styles/tokens";
import { ModalTitle } from "../QueueIntegrationModal/panelKit";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    content: {
      display: "flex",
      flexDirection: "column",
      gap: 14,
      maxHeight: "60vh",
      overflowY: "auto",
    },
    text: {
      margin: 0,
      fontSize: 14,
      lineHeight: 1.55,
      color: t.text,
    },
    linkRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      backgroundColor: t.muted,
      "& .MuiSvgIcon-root": { fontSize: 18, flex: "none", color: t.brand },
    },
    link: {
      color: t.brand,
      fontSize: 14,
      fontWeight: 600,
      wordBreak: "break-all",
    },
    version: {
      margin: 0,
      fontSize: 12.5,
      color: t.textSecondary,
    },
  };
});

/**
 * Termos de uso com aceite (montado no layout autenticado).
 *
 * Consulta `GET /terms/status` ao logar. Se `required`:
 *  - admin → modal não-fechável com o link dos termos e botão "Aceitar" (POST /terms/accept)
 *  - user  → aviso (toast) "O administrador precisa aceitar os termos"; não bloqueia
 * Super nunca recebe `required` (decidido no backend).
 */
const TermsAcceptModal = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [status, setStatus] = useState(null);
  const [accepting, setAccepting] = useState(false);

  const userId = user && user.id;
  const profile = user && user.profile;

  useEffect(() => {
    if (!userId) {
      setStatus(null);
      return;
    }
    let active = true;
    api
      .get("/terms/status")
      .then(({ data }) => {
        if (!active) return;
        setStatus(data && typeof data === "object" ? data : null);
        if (data && data.required && profile !== "admin") {
          toast.warn(i18n.t("terms.adminMustAccept"), {
            toastId: "terms-admin-must-accept",
            autoClose: 8000,
          });
        }
      })
      .catch(() => {
        if (active) setStatus(null);
      });
    return () => {
      active = false;
    };
  }, [userId, profile]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const { data } = await api.post("/terms/accept");
      setStatus(data && typeof data === "object" ? data : { required: false });
      toast.success(i18n.t("terms.accepted"));
    } catch (err) {
      toastError(err);
    } finally {
      setAccepting(false);
    }
  };

  const open = Boolean(status && status.required && profile === "admin");
  if (!open) return null;

  return (
    <Dialog
      open={open}
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      scroll="paper"
      aria-labelledby="terms-accept-dialog"
    >
      <ModalTitle id="terms-accept-dialog" title={i18n.t("terms.title")} />
      <DialogContent className={classes.content}>
        <p className={classes.text}>{i18n.t("terms.description")}</p>
        <div className={classes.linkRow}>
          <OpenInNewIcon />
          <Link
            href={status.termsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.link}
          >
            {i18n.t("terms.readLink")}
          </Link>
        </div>
        {status.termsVersion && (
          <p className={classes.version}>
            {i18n.t("terms.version")}: {status.termsVersion}
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAccept}
          disabled={accepting}
        >
          {accepting ? <CircularProgress size={20} color="inherit" /> : i18n.t("terms.accept")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAcceptModal;
